import { useState } from 'react';
import { ArrowLeftRight, ChevronDown, ChevronUp, Check, X, RefreshCw } from 'lucide-react';
import { formatPrice } from '@/utils';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/store';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface TradeOffer {
  id: string;
  offerer_id: string;
  seller_id: string;
  offerer_username: string;
  offer_description: string;
  cash_included: number;
  counter_description?: string;
  counter_cash?: number;
  status: string;
  created_at: string;
}

interface TradeOfferSectionProps {
  listingId: string;
  sellerId: string;
  tradeFor?: string;
  offers: TradeOffer[];
  onOfferPlaced: () => void;
}

export default function TradeOfferSection({
  listingId, sellerId, tradeFor, offers, onOfferPlaced
}: TradeOfferSectionProps) {
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [showOffers, setShowOffers] = useState(false);
  const [description, setDescription] = useState('');
  const [cashIncluded, setCashIncluded] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [counterText, setCounterText] = useState('');
  const [counterCash, setCounterCash] = useState('');
  const [counteringId, setCounteringId] = useState<string | null>(null);

  const isOwner = user?.id === sellerId;
  const myOffer = offers.find(o => o.offerer_id === user?.id);
  const pendingOffers = offers.filter(o => o.status === 'pending' || o.status === 'countered');

  const handleSubmit = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!description.trim()) { toast.error('Please describe what you want to trade'); return; }

    setIsSubmitting(true);
    try {
      await apiClient.post('/trade-offers', {
        listing_id: listingId,
        seller_id: sellerId,
        offer_description: description,
        cash_included: cashIncluded ? Number(cashIncluded) : 0,
      });
      toast.success('Trade offer sent!');
      setDescription('');
      setCashIncluded('');
      setShowForm(false);
      onOfferPlaced();
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? 'Failed to send offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRespond = async (offerId: string, status: string, counterDesc?: string, counterCashVal?: number) => {
    try {
      await apiClient.put(`/trade-offers/${offerId}/respond`, {
        status,
        counter_description: counterDesc,
        counter_cash: counterCashVal,
      });
      toast.success(
        status === 'accepted' ? 'Offer accepted!' :
        status === 'declined' ? 'Offer declined' :
        status === 'countered' ? 'Counter offer sent!' :
        status === 'counter_accepted' ? 'Counter accepted!' :
        'Counter declined'
      );
      setCounteringId(null);
      setCounterText('');
      setCounterCash('');
      onOfferPlaced();
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? 'Failed to respond');
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
      accepted: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
      declined: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
      countered: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
      counter_accepted: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
      counter_declined: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
      cancelled: 'bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500',
    };
    const labels: Record<string, string> = {
      pending: 'Pending', accepted: 'Accepted', declined: 'Declined',
      countered: 'Counter Offer', counter_accepted: 'Counter Accepted',
      counter_declined: 'Counter Declined', cancelled: 'Cancelled',
    };
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status] ?? 'bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500'}`}>
        {labels[status] ?? status}
      </span>
    );
  };

  return (
    <div className="space-y-3">
      {/* Propose offer button (non-owners) */}
      {!isOwner && (
        <>
          {myOffer ? (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 rounded-xl p-4">
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">Your offer: {statusBadge(myOffer.status)}</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">{myOffer.offer_description}</p>
              {myOffer.cash_included > 0 && <p className="text-xs text-blue-600 mt-1">+ {formatPrice(myOffer.cash_included)} cash</p>}
              {myOffer.counter_description && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">Counter offer from seller:</p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">{myOffer.counter_description}</p>
                  {myOffer.counter_cash && myOffer.counter_cash > 0 && (
                    <p className="text-xs text-blue-600 mt-1">+ {formatPrice(myOffer.counter_cash)} cash</p>
                  )}
                  {myOffer.status === 'countered' && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => handleRespond(myOffer.id, 'counter_accepted')} className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                        <Check className="h-3.5 w-3.5" /> Accept
                      </button>
                      <button onClick={() => handleRespond(myOffer.id, 'counter_declined')} className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                        <X className="h-3.5 w-3.5" /> Decline
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <button
                onClick={() => setShowForm(!showForm)}
                className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeftRight className="h-4 w-4" />
                Propose a Trade
              </button>

              {showForm && (
                <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-xl p-4 space-y-3">
                  {tradeFor && (
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                      <p className="text-xs text-purple-600 font-medium mb-1">Seller is looking for:</p>
                      <p className="text-sm text-purple-800 dark:text-purple-200">{tradeFor}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-gray-500 dark:text-zinc-500 mb-1 block">What are you offering?</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe the card(s) you want to trade (name, condition, set, etc.)"
                      rows={3}
                      className="w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-zinc-500 mb-1 block">Cash to include (optional)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500">$</span>
                      <input
                        type="number"
                        value={cashIncluded}
                        onChange={(e) => setCashIncluded(e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full pl-7 pr-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !description.trim()}
                      className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl font-semibold text-sm transition-colors"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Offer'}
                    </button>
                    <button onClick={() => setShowForm(false)} className="px-4 py-2.5 border rounded-xl text-sm hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Seller view - incoming offers */}
      {isOwner && pendingOffers.length > 0 && (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 shadow-sm rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowOffers(!showOffers)}
            className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-gray-700 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900"
          >
            <span>{pendingOffers.length} pending offer{pendingOffers.length !== 1 ? 's' : ''}</span>
            {showOffers ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showOffers && (
            <div className="divide-y divide-gray-200 dark:divide-zinc-800">
              {pendingOffers.map((offer) => (
                <div key={offer.id} className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">{offer.offerer_username}</p>
                    {statusBadge(offer.status)}
                  </div>
                  <p className="text-sm text-gray-700 dark:text-zinc-300">{offer.offer_description}</p>
                  {offer.cash_included > 0 && (
                    <p className="text-xs text-green-600 font-medium">+ {formatPrice(offer.cash_included)} cash included</p>
                  )}

                  {offer.status === 'pending' && (
                    <>
                      {counteringId === offer.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={counterText}
                            onChange={(e) => setCounterText(e.target.value)}
                            placeholder="Describe your counter offer..."
                            rows={2}
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                          />
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 text-sm">$</span>
                            <input
                              type="number"
                              value={counterCash}
                              onChange={(e) => setCounterCash(e.target.value)}
                              placeholder="Cash to include (optional)"
                              className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleRespond(offer.id, 'countered', counterText, counterCash ? Number(counterCash) : undefined)}
                              disabled={!counterText.trim()}
                              className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                            >
                              Send Counter
                            </button>
                            <button onClick={() => setCounteringId(null)} className="px-3 py-2 border rounded-lg text-sm hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button onClick={() => handleRespond(offer.id, 'accepted')} className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                            <Check className="h-3.5 w-3.5" /> Accept
                          </button>
                          <button onClick={() => setCounteringId(offer.id)} className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                            <RefreshCw className="h-3.5 w-3.5" /> Counter
                          </button>
                          <button onClick={() => handleRespond(offer.id, 'declined')} className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                            <X className="h-3.5 w-3.5" /> Decline
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}